import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';
import { LINKED_RECORD_PARAM, LINKED_TAB_PARAM, useUrlChoice } from '@/hooks/use-linked-record';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';

export type QueueOption<TValue extends string> = {
  value: TValue;
  /** Translation key of the label. */
  label: string;
  /** How many items are in this queue; nothing shows while it is unknown. */
  count?: number;
};

/** The URL param of a list's queue (`?status=`). */
export const QUEUE_PARAM = 'status';

/**
 * Which queue of a review list is open, kept in the URL (`?status=`): `defaultQueue` (usually Pending)
 * unless the page was linked to one request (a notification), which opens `linkedQueue` (usually All)
 * so a request already decided still shows.
 */
export const useQueueStatus = <TValue extends string>(
  values: readonly TValue[],
  defaultQueue: TValue,
  linkedQueue: TValue
) => {
  const [searchParams] = useSearchParams();
  const isLinked = Boolean(searchParams.get(LINKED_RECORD_PARAM));
  // Archived was a tab of the old manager pages: links that still say `?tab=archived` open it.
  const archived = 'Archived' as TValue;
  const wasArchivedTab =
    searchParams.get(LINKED_TAB_PARAM) === 'archived' && values.includes(archived);
  return useUrlChoice<TValue>(
    QUEUE_PARAM,
    values,
    wasArchivedTab ? archived : isLinked ? linkedQueue : defaultQueue
  );
};

/**
 * A review list's queue switch (HR Module Prototype, Component Map › Queue status): an outline
 * toggle-group in the table toolbar, one item per status plus All, each with its count. The selected
 * item's count is tinted with the primary colour.
 */
export const QueueStatusFilter = <TValue extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: readonly QueueOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  className?: string;
}) => {
  const { t } = useTranslation();
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      size="sm"
      value={value}
      // Clicking the selected item would clear the value; one queue is always open.
      onValueChange={(next) => next && onChange(next as TValue)}
      aria-label={t('STATUS')}
      className={cn('max-w-full overflow-x-auto [scrollbar-width:none]', className)}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <ToggleGroupItem key={option.value} value={option.value} className="shrink-0 gap-1.5">
            {t(option.label)}
            {option.count !== undefined && (
              <span
                className={cn(
                  'inline-grid h-[18px] min-w-5 place-items-center rounded-full px-1.5 text-[11px] tabular-nums',
                  isSelected
                    ? 'bg-[color-mix(in_srgb,oklch(var(--primary))_14%,transparent)] text-primary'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {option.count}
              </span>
            )}
          </ToggleGroupItem>
        );
      })}
    </ToggleGroup>
  );
};
